import React from "react"
import {
  formatChargeType,
  formatInvoiceDate,
  getServiceDescription,
  type InvoiceLineItem,
} from "./invoiceUtils.ts"

type InvoiceLineItemsProps = {
  lineItems?: InvoiceLineItem[]
  invoiceType?: string
  servicePlanName: string
  subtotal: number
  discountPercentage: number
  totalAmount: number
  totalPaid: number
  remainingAmount: number
  isSubscription: boolean
  billingStartDate?: string
  billingEndDate?: string
  issueDate?: string
}

type ResolvedLine = {
  key: string
  charge: string
  description: string
  periodLabel?: string
  quantity: number
  unitPrice: number
  lineTotal: number
}

function resolveLines({
  lineItems,
  invoiceType,
  servicePlanName,
  subtotal,
  isSubscription,
  billingStartDate,
  billingEndDate,
  issueDate,
}: Pick<
  InvoiceLineItemsProps,
  | "lineItems"
  | "invoiceType"
  | "servicePlanName"
  | "subtotal"
  | "isSubscription"
  | "billingStartDate"
  | "billingEndDate"
  | "issueDate"
>): ResolvedLine[] {
  if (lineItems && lineItems.length > 0) {
    return lineItems.map((item, index) => {
      const charge = item.charge_type || item.item_type || invoiceType || "charge"
      const periodStart =
        item.billing_start_date || (charge === "subscription" ? billingStartDate : null)
      const periodEnd = item.billing_end_date || (charge === "subscription" ? billingEndDate : null)
      return {
        key: item.id || String(index),
        charge: formatChargeType(charge),
        description: item.description,
        periodLabel:
          periodStart && periodEnd
            ? `Period: ${formatInvoiceDate(periodStart)} — ${formatInvoiceDate(periodEnd)}`
            : undefined,
        quantity: item.quantity,
        unitPrice: Number(item.unit_price || 0),
        lineTotal: Number(item.line_total || 0),
      }
    })
  }

  const periodLabel = isSubscription
    ? `Period: ${formatInvoiceDate(billingStartDate || "")} — ${formatInvoiceDate(billingEndDate || "")}`
    : issueDate
      ? `Date: ${formatInvoiceDate(issueDate)}`
      : undefined

  return [
    {
      key: "fallback",
      charge: formatChargeType(invoiceType),
      description: getServiceDescription(invoiceType, servicePlanName),
      periodLabel,
      quantity: 1,
      unitPrice: subtotal,
      lineTotal: subtotal,
    },
  ]
}

function MobileLineCards({ lines }: { lines: ResolvedLine[] }) {
  return (
    <div className="space-y-3 md:hidden print:hidden">
      {lines.map((line) => (
        <div key={line.key} className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800">{line.description}</p>
              <p className="mt-0.5 text-xs capitalize text-slate-500">{line.charge}</p>
              {line.periodLabel ? <p className="mt-1 text-xs text-slate-500">{line.periodLabel}</p> : null}
            </div>
            <p className="shrink-0 text-sm font-bold tabular-nums text-slate-800">
              PKR {line.lineTotal.toLocaleString()}
            </p>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-3 text-xs text-slate-500">
            <span>Qty {line.quantity}</span>
            <span className="tabular-nums">PKR {line.unitPrice.toLocaleString()} ea</span>
          </div>
        </div>
      ))}
    </div>
  )
}

function DesktopLineTable({
  lines,
  discountPercentage,
  subtotal,
  totalAmount,
  totalPaid,
  remainingAmount,
}: {
  lines: ResolvedLine[]
  discountPercentage: number
  subtotal: number
  totalAmount: number
  totalPaid: number
  remainingAmount: number
}) {
  return (
    <div className="hidden overflow-x-auto md:block print:block">
      <table className="w-full min-w-[640px]">
        <thead>
          <tr className="bg-table-header">
            <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-deep-ocean">
              Charge
            </th>
            <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-deep-ocean">
              Description
            </th>
            <th className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wider text-deep-ocean">
              Qty
            </th>
            <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-deep-ocean">
              Price
            </th>
            <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-deep-ocean">
              Amount
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {lines.map((line) => (
            <tr key={line.key} className="hover:bg-slate-50/50">
              <td className="whitespace-nowrap px-4 py-3 text-xs capitalize text-slate-600">{line.charge}</td>
              <td className="px-4 py-3">
                <p className="text-sm font-semibold text-slate-800">{line.description}</p>
                {line.periodLabel ? <p className="mt-0.5 text-xs text-slate-500">{line.periodLabel}</p> : null}
              </td>
              <td className="px-3 py-3 text-center text-sm text-slate-700">{line.quantity}</td>
              <td className="whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums text-slate-600">
                PKR {line.unitPrice.toLocaleString()}
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-bold tabular-nums text-slate-800">
                PKR {line.lineTotal.toLocaleString()}
              </td>
            </tr>
          ))}
          {discountPercentage > 0 ? (
            <tr className="bg-emerald-50/50">
              <td className="px-4 py-3 text-slate-600" colSpan={4}>
                Discount ({discountPercentage}%)
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-right font-semibold tabular-nums text-emerald-600">
                -PKR {(subtotal * (discountPercentage / 100)).toFixed(2)}
              </td>
            </tr>
          ) : null}
        </tbody>
        <tfoot className="hidden print:table-footer-group">
          <tr className="bg-deep-ocean">
            <td className="px-4 py-3 text-right text-sm font-bold uppercase tracking-wide text-white" colSpan={4}>
              Total Amount
            </td>
            <td className="px-4 py-3 text-right">
              <span className="text-lg font-bold tabular-nums text-white">
                PKR {totalAmount.toLocaleString()}
              </span>
            </td>
          </tr>
          {totalPaid > 0 ? (
            <>
              <tr className="bg-emerald-50/50">
                <td
                  className="px-4 py-3 text-right text-sm font-bold uppercase tracking-wide text-emerald-700"
                  colSpan={4}
                >
                  Amount Paid
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-base font-bold tabular-nums text-emerald-700">
                    PKR {totalPaid.toLocaleString()}
                  </span>
                </td>
              </tr>
              <tr className={remainingAmount > 0 ? "bg-red-50/50" : "bg-slate-50/50"}>
                <td
                  className={`px-4 py-3 text-right text-sm font-bold uppercase tracking-wide ${remainingAmount > 0 ? "text-red-700" : "text-slate-700"}`}
                  colSpan={4}
                >
                  Balance Due
                </td>
                <td className="px-4 py-3 text-right">
                  <span
                    className={`text-base font-bold tabular-nums ${remainingAmount > 0 ? "text-red-700" : "text-slate-700"}`}
                  >
                    PKR {remainingAmount.toLocaleString()}
                  </span>
                </td>
              </tr>
            </>
          ) : null}
        </tfoot>
      </table>
    </div>
  )
}

function MobileTotals({
  discountPercentage,
  subtotal,
  totalAmount,
  totalPaid,
  remainingAmount,
}: {
  discountPercentage: number
  subtotal: number
  totalAmount: number
  totalPaid: number
  remainingAmount: number
}) {
  return (
    <div className="mt-3 space-y-2 border-t border-slate-200 pt-3 md:hidden print:hidden">
      {discountPercentage > 0 ? (
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600">Discount ({discountPercentage}%)</span>
          <span className="font-semibold tabular-nums text-emerald-600">
            -PKR {(subtotal * (discountPercentage / 100)).toFixed(2)}
          </span>
        </div>
      ) : null}
      <div className="flex items-center justify-between text-sm">
        <span className="font-semibold text-slate-700">Total Amount</span>
        <span className="text-base font-bold tabular-nums text-deep-ocean">PKR {totalAmount.toLocaleString()}</span>
      </div>
      {totalPaid > 0 ? (
        <>
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold text-emerald-700">Amount Paid</span>
            <span className="font-bold tabular-nums text-emerald-700">PKR {totalPaid.toLocaleString()}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className={`font-semibold ${remainingAmount > 0 ? "text-red-700" : "text-slate-700"}`}>
              Balance Due
            </span>
            <span
              className={`text-base font-bold tabular-nums ${remainingAmount > 0 ? "text-red-700" : "text-slate-700"}`}
            >
              PKR {remainingAmount.toLocaleString()}
            </span>
          </div>
        </>
      ) : null}
    </div>
  )
}

export function InvoiceLineItems(props: InvoiceLineItemsProps) {
  const lines = resolveLines(props)

  return (
    <div className="border-b border-slate-100 py-6 sm:py-8">
      <div className="overflow-hidden rounded-xl border border-slate-200">
        <MobileLineCards lines={lines} />
        <DesktopLineTable
          lines={lines}
          discountPercentage={props.discountPercentage}
          subtotal={props.subtotal}
          totalAmount={props.totalAmount}
          totalPaid={props.totalPaid}
          remainingAmount={props.remainingAmount}
        />
        <div className="px-4 pb-4 md:hidden print:hidden">
          <MobileTotals
            discountPercentage={props.discountPercentage}
            subtotal={props.subtotal}
            totalAmount={props.totalAmount}
            totalPaid={props.totalPaid}
            remainingAmount={props.remainingAmount}
          />
        </div>
      </div>
    </div>
  )
}
